import {
  buildCollection,
  buildProperty,
  EntityReference,
  type AuthController,
  type CollectionActionsProps,
  type EntityCallbacks,
  type EntityOnPreSaveProps,
  type EntityValues,
} from "@firecms/core";
import { canAdminEvent, getEventPath, type EventCollectionId } from "../utils";
import { LocaleName, Locales } from "../localization";
import type { Story } from "./stories";
import { FunctionButton } from "../actions/FunctionButton";
import { toCallableWorkspaceParams } from "../workspace/callableParams";
import { AddTeamsButton } from "../actions/AddTeamsButton";
import { buildAssignedParticipantsPath } from "./participants";
import { NAV_GROUP_SESSION } from "./navigationGroups";
import { buildTeamNotificationsCollection } from "./notifications";

export type Team = {
  isFull: boolean;
  isLocked: boolean;
  locale: typeof Locales | "any";
} & TeamPublicProps;

type TeamPublicProps = {
  teamNumber: number;
  membersCount: number;
  maxMembers: number;
  profileAvg: [number, number, number, number];
};

export type TeamUser = {
  uid: string;
  participantRef: EntityReference;
};

export const buildTeamsPath = (id: EventCollectionId) => `${getEventPath(id)}/teams`;

export const buildTeamUsersCollection = (
  id: EventCollectionId
) => {
  return buildCollection<TeamUser>({
    name: "Members",
    singularName: "Member",
    description:
      "People assigned to this team in the current session.",
    path: "teamUsers",
    id: "team_users",
    icon: "EmojiPeople",
    group: NAV_GROUP_SESSION,
    defaultSize: "xs",
    permissions: () => ({
      edit: false,
      create: false,
      delete: false,
      read: true,
    }),
    properties: {
      uid: buildProperty({
        name: "User ID",
        description: "Firebase Auth uid linked to this team membership.",
        dataType: "string",
        readOnly: true,
      }),
      participantRef: buildProperty({
        name: "Participant",
        description: "Player slot assigned to this team member.",
        dataType: "reference",
        path: buildAssignedParticipantsPath(id),
        readOnly: true,
      }),
    },
    Actions: ({ selectionController }) => [
      <FunctionButton
        functionName={"removeParticipantFromTeam"}
        selectionController={selectionController}
        label={"Remove from team"}
        loadingLabel={"Removing from team"}
        params={(ids, selectedEntities) => ({
          ...toCallableWorkspaceParams(ids),
          participantId: selectedEntities[0]!.values.participantRef?.id ?? selectedEntities[0]!.id,
        })}
        multiple={false}
      />,
    ],
  });
};

const teamsCallbacks: EntityCallbacks<Team> = {
  onPreSave({
    values,
  }: EntityOnPreSaveProps<Team>): Partial<EntityValues<Team>> {
    return {
      ...values,
      membersCount: values.membersCount ?? 0,
      profileAvg: values.profileAvg ?? [1, 1, 1, 1],
    };
  },
};

export const buildTeamsCollection = (
  id: EventCollectionId,
  hideFromNavigation?: boolean
) => {
  return buildCollection<Team>({
    name: "Teams",
    singularName: "Team",
    description:
      "Groups participants belong to during this session. Many levels award points to the whole team, not only to individuals.",
    path: buildTeamsPath(id),
    id: "teams",
    icon: "Diversity3",
    group: NAV_GROUP_SESSION,
    callbacks: teamsCallbacks,
    hideFromNavigation,
    defaultSize: "xs",
    initialSort: [
      "teamNumber", "asc",
    ],
    permissions: ({ authController }: { authController: AuthController }) => ({
      edit: canAdminEvent(id, authController.extra),
      create: false,
      delete: false,
      read: true,
    }),
    subcollections: [
      buildTeamUsersCollection(id),
      buildTeamNotificationsCollection(id),
    ],
    properties: {
      teamNumber: buildProperty({
        name: "Team number",
        description:
          "Ordinal number of this team in the session (1, 2, 3…). Used for ordering and auto-matching.",
        validation: { required: true, min: 1 },
        dataType: "number",
      }),
      membersCount: buildProperty({
        name: "Members",
        description: "How many participants are currently in this team.",
        dataType: "number",
        defaultValue: 0,
        readOnly: true,
      }),
      maxMembers: buildProperty({
        name: "Max members",
        description:
          "Capacity for this team—usually copied from the session’s “Max members per team”. The team is marked full at this number.",
        validation: { required: false, min: 1 },
        dataType: "number",
      }),
      isFull: buildProperty({
        name: "Full",
        description: "Set automatically when the team reaches max members.",
        validation: { required: false },
        dataType: "boolean",
        readOnly: true,
        defaultValue: false,
      }),
      isLocked: buildProperty({
        name: "Locked",
        description:
          "When on, participants cannot join or leave this team. Use it to freeze assignments before play starts.",
        validation: { required: false },
        dataType: "boolean",
        defaultValue: false,
      }),
      locale: buildProperty({
        name: "Locale",
        description:
          "Language locked for auto-matching. A specific language only receives that language. “Any” is still open: the first specific-language participant claims an empty team and locks it.",
        validation: { required: true },
        dataType: "string",
        enumValues: {
          any: "Any",
          [Locales.ES]: LocaleName[Locales.ES],
          [Locales.EN]: LocaleName[Locales.EN],
          [Locales.FR]: LocaleName[Locales.FR],
          [Locales.PT]: LocaleName[Locales.PT],
        },
      }),
      profileAvg: buildProperty({
        name: "Profile average",
        description:
          "Running average of member onboarding prefs [pace, collab, motivation, participation] used for auto-matching.",
        dataType: "array",
        of: {
          dataType: "number",
        },
        readOnly: true,
        defaultValue: [1, 1, 1, 1],
      }),
    },
    Actions: ({ selectionController: _selectionController }: CollectionActionsProps<Story>) => [
      <AddTeamsButton key="add-teams" />,
    ],
  });
};
